import React, { useEffect } from "react";
import {
  BarChart3,
  Microscope,
  Check,
  Star,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import { ResultType } from "../types";
import { ProductImage } from "./ProductCard";

interface ResultPageProps {
  result: ResultType;
  onRetake: () => void;
  trackEvent: (name: string, params?: Record<string, any>) => void;
}

const ResultPage: React.FC<ResultPageProps> = ({
  result,
  onRetake,
  trackEvent,
}) => {
  useEffect(() => {
    trackEvent(`view_result`, { type: result.type });
  }, [result, trackEvent]);

  return (
    <div className="bg-white min-h-screen pt-14 pb-24 font-sans text-slate-900">
      {/* Analysis Header */}
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-12 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-slate-200 mb-6 shadow-sm">
            <BarChart3 size={24} className="text-slate-700" />
          </div>

          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="px-3 py-1 bg-slate-900 text-white text-xs font-bold rounded-full">
              {result.type}
            </span>
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              Analysis Complete
            </h2>
          </div>
          <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
            {result.headline}
          </h1>

          <div className="bg-white border border-slate-200 p-6 text-left max-w-xl mx-auto shadow-sm rounded-xl">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-sm uppercase tracking-wide border-b border-slate-100 pb-2">
              <Microscope size={16} /> Formulation Strategy
            </h4>
            <ul className="space-y-3">
              {result.summary.map((point, i) => (
                <li
                  key={i}
                  className="text-slate-700 text-sm flex items-start gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-[10px] font-bold mt-0.5 shrink-0">
                    {i + 1}
                  </div>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Prescription List */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
            Recommended Products
          </h3>
          <div className="h-px bg-slate-200 flex-1"></div>
        </div>

        <div className="space-y-6">
          {result.products.map((product, idx) => (
            <div
              key={idx}
              className={`bg-white border ${
                product.isHero
                  ? "border-orange-200 ring-1 ring-orange-100"
                  : "border-slate-200"
              } rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300`}
            >
              {/* Desktop: Row Layout / Mobile: Stack Layout */}
              <div className="flex flex-col md:flex-row">
                {/* 1. Image & Basic Info Section */}
                <div className="p-6 md:w-1/3 border-b md:border-b-0 md:border-r border-slate-100 flex flex-row md:flex-col gap-4 items-center md:items-start bg-slate-50/50">
                  <div className="w-20 h-20 md:w-32 md:h-32 bg-white rounded-lg border border-slate-200 p-2 flex-shrink-0">
                    <ProductImage
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain mix-blend-multiply"
                    />
                  </div>
                  <div className="flex-1 text-left">
                    {product.isHero && (
                      <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-bold uppercase tracking-wide rounded mb-2">
                        Top Pick
                      </span>
                    )}
                    <div className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                      {product.brand}
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-slate-900 leading-snug mb-2">
                      {product.name}
                    </h3>
                    <div className="text-sm font-medium text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded inline-block">
                      {product.price}{" "}
                      <span className="text-slate-300 mx-1">|</span>{" "}
                      {product.size}
                    </div>
                  </div>
                </div>

                {/* 2. Detailed Columns Section */}
                <div className="flex-1 p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Column: Best For & Features */}
                  <div className="space-y-4">
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        Best For
                      </div>
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-800 text-xs font-bold rounded-lg border border-blue-100">
                        {product.bestFor}
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        Checklist
                      </div>
                      <ul className="space-y-1.5">
                        {product.features.map((feat, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-xs md:text-sm text-slate-600"
                          >
                            <Check
                              size={14}
                              className="text-green-500 mt-0.5 shrink-0"
                            />
                            {feat}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Column: Review & Action */}
                  <div className="flex flex-col h-full">
                    <div className="mb-6 flex-1">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                        User Review
                      </div>
                      {product.ugc && (
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 relative">
                          <div className="flex gap-0.5 mb-1.5">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={10}
                                className="text-yellow-400 fill-yellow-400"
                              />
                            ))}
                          </div>
                          <p className="text-xs italic text-slate-600 leading-relaxed">
                            "{product.ugc}"
                          </p>
                          <p className="text-[10px] text-slate-400 mt-1 font-bold text-right">
                            — {product.ugcAuthor}
                          </p>
                        </div>
                      )}
                    </div>

                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackEvent("product_click", {
                          brand: product.brand,
                          name: product.name,
                        });
                        trackEvent("amazon_redirect", { url: product.link });
                      }}
                      className={`w-full py-3 px-4 rounded-lg text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-all shadow-sm ${
                        product.isHero
                          ? "bg-orange-600 text-white hover:bg-orange-700 hover:shadow-md"
                          : "bg-slate-900 text-white hover:bg-slate-800 hover:shadow-md"
                      }`}
                    >
                      Buy on Amazon <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer CTA */}
      <div className="text-center mt-12 mb-12">
        <button
          onClick={onRetake}
          className="inline-flex items-center text-sm font-bold text-slate-400 hover:text-slate-900 uppercase tracking-widest transition-colors"
        >
          Restart Analysis <ArrowRight size={14} className="ml-2" />
        </button>
      </div>
    </div>
  );
};

export default ResultPage;
